import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, CalendarCog, Eye, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/date-effet`;
const fr = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');

export const finAuto = (produit, effet) => {
  if (!effet) return '';
  const [y, m, d] = effet.split('-').map(Number);
  const fin = produit === 'domium' ? new Date(Date.UTC(y + 10, m - 1, d)) : new Date(Date.UTC(y + 1, m - 1, d - 1));
  if (produit === 'domium' && fin.getUTCMonth() !== m - 1) fin.setUTCDate(0);
  return fin.toISOString().slice(0, 10);
};

const Comparatif = ({ avant, effet, fin }) => (
  <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="date-effet-comparatif">
    {[['Date d\'effet', avant.date_effet, effet], ['Expiration', avant.date_fin, fin]].map(([label, a, b]) => (
      <div key={label}>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="mt-0.5 flex items-center gap-1.5 font-medium">
          <span className="text-slate-500 line-through decoration-slate-300">{fr(a)}</span>
          <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
          <span className={a === b ? 'text-slate-700' : 'text-[#0a3d62]'}>{fr(b)}</span>
        </p>
      </div>
    ))}
  </div>
);

const Historique = ({ items }) => (items?.length ? (
  <div className="text-xs text-slate-500" data-testid="date-effet-historique">
    <p className="mb-1 font-semibold text-slate-600">Rééditions précédentes</p>
    {items.slice(0, 5).map((h) => (
      <p key={h.id}>{new Date(h.at).toLocaleDateString('fr-FR')} · {fr(h.ancienne_date_effet)} → {fr(h.date_effet)} (fin {fr(h.date_fin)}) · {h.par}{h.envoi ? ` · e-mail ${h.envoi === 'envoye' ? 'envoyé' : 'en échec'}` : ''}</p>
    ))}
  </div>
) : null);

const useDateEffet = (produit, ident, open) => {
  const { getAuthHeader } = useAuth();
  const [avant, setAvant] = useState(null);
  const [effet, setEffet] = useState('');
  const [fin, setFin] = useState('');
  const [manuel, setManuel] = useState(false);
  const [pdf, setPdf] = useState(null);
  const [apercuDe, setApercuDe] = useState('');
  const [busy, setBusy] = useState('');
  const [email, setEmail] = useState(false);
  useEffect(() => {
    if (!open) return;
    setAvant(null); setPdf(null); setApercuDe(''); setManuel(false); setEmail(false);
    axios.get(`${API}/${produit}/${encodeURIComponent(ident)}`, { headers: getAuthHeader() })
      .then((r) => { setAvant(r.data); setEffet(r.data.date_effet || ''); setFin(r.data.date_fin || ''); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Chargement impossible'));
  }, [open, produit, ident]); // eslint-disable-line react-hooks/exhaustive-deps
  const changerEffet = (v) => { setEffet(v); if (!manuel || !avant?.fin_modifiable) setFin(finAuto(produit, v)); };
  const changerFin = (v) => { setFin(v); setManuel(true); };
  const cle = `${effet}|${fin}`;
  const corps = { date_effet: effet, date_fin: avant?.fin_modifiable ? fin : undefined };
  const voir = async () => {
    setBusy('apercu');
    try {
      const r = await axios.post(`${API}/${produit}/${encodeURIComponent(ident)}/apercu`, corps, { headers: getAuthHeader(), responseType: 'blob' });
      setPdf((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(r.data); });
      setApercuDe(cle);
    } catch (e) {
      toast.error(e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : 'Aperçu impossible');
    } finally { setBusy(''); }
  };
  const valider = async (onDone) => {
    setBusy('valider');
    try {
      const r = await axios.post(`${API}/${produit}/${encodeURIComponent(ident)}/valider`, { ...corps, envoyer_email: email }, { headers: getAuthHeader() });
      toast.success(r.data.message + (r.data.envoi === 'envoye' ? ' — envoyée au membre' : r.data.envoi === 'echec' ? ' — e-mail en échec' : ''));
      onDone?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Réédition impossible'); } finally { setBusy(''); }
  };
  return { avant, effet, fin, changerEffet, changerFin, pdf, apercuAJour: apercuDe === cle && !!pdf, busy, email, setEmail, voir, valider };
};

export const DateEffetDialog = ({ produit, ident, open, onOpenChange, onDone }) => {
  const s = useDateEffet(produit, ident, open);
  const inchange = s.avant && s.effet === s.avant.date_effet && s.fin === s.avant.date_fin;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="date-effet-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarCog className="h-5 w-5 text-[#0a3d62]" /> Modifier la date d&apos;effet{produit !== 'domium' ? ' et d\u2019expiration' : ''}</DialogTitle>
        </DialogHeader>
        {!s.avant ? <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin text-slate-400" /> : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600" data-testid="date-effet-objet">{s.avant.libelle} · <b>{s.avant.nom || s.avant.reference}</b> · {s.avant.reference}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm">Nouvelle date d&apos;effet
                <input type="date" value={s.effet} onChange={(e) => s.changerEffet(e.target.value)} className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2" data-testid="date-effet-input" />
              </label>
              <label className="text-sm">Date d&apos;expiration <span className="text-xs text-slate-400">({s.avant.regle})</span>
                <input type="date" value={s.fin} disabled={!s.avant.fin_modifiable} onChange={(e) => s.changerFin(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 disabled:bg-slate-100" data-testid="date-fin-input" />
              </label>
            </div>
            <Comparatif avant={s.avant} effet={s.effet} fin={s.fin} />
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" onClick={s.voir} disabled={!s.effet || !!s.busy || inchange} data-testid="date-effet-apercu-btn">
                {s.busy === 'apercu' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Eye className="mr-1.5 h-4 w-4" />} Générer l&apos;aperçu
              </Button>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={s.email} disabled={!s.avant.email} onChange={(e) => s.setEmail(e.target.checked)} data-testid="date-effet-email-checkbox" />
                Envoyer la nouvelle attestation au membre par e-mail{s.avant.email ? ` (${s.avant.email})` : ' (aucune adresse)'}
              </label>
            </div>
            {s.pdf && <iframe title="Aperçu de l'attestation" src={s.pdf} className="h-[55vh] w-full rounded-lg border" data-testid="date-effet-apercu-pdf" />}
            {!s.apercuAJour && !inchange && <p className="text-xs text-amber-700" data-testid="date-effet-apercu-requis">Générez l&apos;aperçu des dates saisies avant de valider.</p>}
            <div className="flex items-center justify-between gap-3 border-t pt-3">
              <Historique items={s.avant.historique} />
              <Button onClick={() => s.valider((r) => { onOpenChange(false); onDone?.(r); })} disabled={!s.apercuAJour || !!s.busy}
                className="ml-auto bg-emerald-600 hover:bg-emerald-700" data-testid="date-effet-valider-btn">
                {s.busy === 'valider' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />} Valider et rééditer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const DateEffetButton = ({ produit, ident, onDone, compact = false, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setOpen(true); }} title={produit === 'domium' ? "Modifier la date d'effet" : "Modifier la date d'effet et d'expiration"}
        className={compact ? 'h-7 px-2 text-xs border-sky-300 text-[#0a3d62] hover:bg-sky-50' : 'gap-1.5 text-xs'} data-testid={testId || 'date-effet-btn'}>
        <CalendarCog className="h-3.5 w-3.5" />{compact ? '' : <span className="ml-1">{produit === 'domium' ? 'Modifier la date d\u2019effet' : 'Modifier les dates d\u2019effet et d\u2019expiration'}</span>}
      </Button>
      {open && <DateEffetDialog produit={produit} ident={ident} open={open} onOpenChange={setOpen} onDone={onDone} />}
    </>
  );
};
